import { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { config } from '@fortawesome/fontawesome-svg-core';
import clsx from 'clsx';
import '@fortawesome/fontawesome-svg-core/styles.css'; // SSR-safe: static CSS
import '@/elements/Icon/Icon.styles.css';

import { IProps } from '@/elements/Icon/Icon.interface';

// Regulars
import {
    faArrowLeft, faArrowRight, faBars, faBook, faBottleDroplet, faBriefcase, faBullhorn,
    faBullseye, faCalendar, faCalendarDays, faCalendarStar, faCamera, faCar, faChalkboardUser,
    faChartLine, faCheck, faChevronDown, faChevronLeft, faChevronRight, faChevronUp, faChild,
    faChurch, faClipboardList, faDisplayChartUpCircleDollar, faDownload, faEnvelope,
    faEnvelopeOpenText, faExternalLink, faFaceSmile, faFamily, faFile, faFileInvoiceDollar,
    faFileSignature, faFolder, faGift, faGraduationCap, faHand, faHandHeart, faHandHoldingDollar,
    faHandHoldingHeart, faHandHoldingSeedling, faHands, faHandshake, faHandshakeAngle,
    faHandsHelping, faHandsHoldingChild, faHandsHoldingDollar, faHeadSideBrain, faHospitalUser,
    faHouse, faHouseChimney, faHouseChimneyHeart, faHouseCircleCheck, faHouseUser, faInfoCircle,
    faKey, faLaptopFile, faLightbulb, faList, faLocationDot, faMagnifyingGlass, faMailboxFlagUp,
    faMobile, faPartyHorn, faPeopleGroup, faPerson, faPersonCane, faPersonShelter, faPhone,
    faReceipt, faRoute, faScaleBalanced, faScreenUsers, faSearch, faShieldHeart, faStar,
    faUniversalAccess, faUsers, faVideo, faXmark,
} from '@fortawesome/pro-regular-svg-icons';

// Brands
import {
    faCcAmex, faCcMastercard, faCcPaypal, faCcVisa, faFacebook,
    faInstagram, faLinkedin, faXTwitter, faYoutube,
} from '@fortawesome/free-brands-svg-icons';

// IMPORTANT: do this once globally if possible
config.autoAddCss = false;

export const ICON_MAP = {
    'external-link': faExternalLink,
    'cc-amex': faCcAmex,
    'cc-mastercard': faCcMastercard,
    'cc-paypal': faCcPaypal,
    'cc-visa': faCcVisa,
    'arrow-left': faArrowLeft,
    'arrow-right': faArrowRight,
    bars: faBars,
    book: faBook,
    'bottle-droplet': faBottleDroplet,
    briefcase: faBriefcase,
    bullhorn: faBullhorn,
    bullseye: faBullseye,
    calendar: faCalendar,
    'calendar-days': faCalendarDays,
    'calendar-star': faCalendarStar,
    camera: faCamera,
    car: faCar,
    'chalkboard-user': faChalkboardUser,
    'chart-line': faChartLine,
    check: faCheck,
    'chevron-down': faChevronDown,
    'chevron-left': faChevronLeft,
    'chevron-right': faChevronRight,
    'chevron-up': faChevronUp,
    child: faChild,
    church: faChurch,
    'clipboard-list': faClipboardList,
    'display-chart-up-circle-dollar': faDisplayChartUpCircleDollar,
    download: faDownload,
    envelope: faEnvelope,
    'envelope-open-text': faEnvelopeOpenText,
    facebook: faFacebook,
    'face-smile': faFaceSmile,
    family: faFamily,
    file: faFile,
    'file-invoice-dollar': faFileInvoiceDollar,
    'file-signature': faFileSignature,
    folder: faFolder,
    gift: faGift,
    'graduation-cap': faGraduationCap,
    hand: faHand,
    'hand-heart': faHandHeart,
    'hand-holding-dollar': faHandHoldingDollar,
    'hand-holding-heart': faHandHoldingHeart,
    'hand-holding-seedling': faHandHoldingSeedling,
    hands: faHands,
    handshake: faHandshake,
    'handshake-angle': faHandshakeAngle,
    'hands-helping': faHandsHelping,
    'hands-holding-child': faHandsHoldingChild,
    'hands-holding-dollar': faHandsHoldingDollar,
    'head-side-brain': faHeadSideBrain,
    home: faHouse,
    'hospital-user': faHospitalUser,
    house: faHouse,
    'house-chimney': faHouseChimney,
    'house-chimney-heart': faHouseChimneyHeart,
    'house-circle-check': faHouseCircleCheck,
    'house-user': faHouseUser,
    'info-circle': faInfoCircle,
    instagram: faInstagram,
    key: faKey,
    'laptop-file': faLaptopFile,
    lightbulb: faLightbulb,
    linkedin: faLinkedin,
    list: faList,
    'location-dot': faLocationDot,
    'magnifying-glass': faMagnifyingGlass,
    'mailbox-flag-up': faMailboxFlagUp,
    mobile: faMobile,
    'party-horn': faPartyHorn,
    'people-group': faPeopleGroup,
    person: faPerson,
    'person-cane': faPersonCane,
    'person-shelter': faPersonShelter,
    phone: faPhone,
    receipt: faReceipt,
    route: faRoute,
    'scale-balanced': faScaleBalanced,
    'screen-users': faScreenUsers,
    search: faSearch,
    'shield-heart': faShieldHeart,
    star: faStar,
    'universal-access': faUniversalAccess,
    users: faUsers,
    video: faVideo,
    xmark: faXmark,
    'x-twitter': faXTwitter,
    youtube: faYoutube,
} as const;

type IconKey = keyof typeof ICON_MAP;
type IconRef = SVGSVGElement;

// Extend IProps so aria-label can be passed and inferred
type Props = Omit<IProps, 'icon'> & {
    icon: IconKey;
    'aria-label'?: string;
};

const Icon = forwardRef<IconRef, Props>(function Icon(
    { icon, className, 'aria-label': ariaLabel, ...rest },
    ref
) {
    const faicon = ICON_MAP[icon];

    if (!faicon) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn(`Icon '${icon}' not found`);
        }
        return null;
    }

    const a11yProps = ariaLabel
        ? { 'aria-label': ariaLabel }
        : { 'aria-hidden': true as const };

    return (
        <FontAwesomeIcon
            ref={ref}
            icon={faicon}
            className={clsx('icon', className)}
            {...a11yProps}
            {...rest}
        />
    );
});

export default Icon;
